import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/* ------------------------------------------------------------------
   GLOBAL CART WATCHERS
   - Patches fetch/XMLHttpRequest once per page.
   - On any successful non-GET to /cart/*, fetches /cart.js
     and dispatches a custom event: 'cart:count-changed'.
   ------------------------------------------------------------------ */

async function __getServerItemCount() {
  try {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.item_count ?? 0;
  } catch {
    return null;
  }
}

function __installCartNetworkWatchers() {
  if (window.__CART_WATCHERS_INSTALLED__) return;
  window.__CART_WATCHERS_INSTALLED__ = true;

  let t;
  const notify = async () => {
    clearTimeout(t);
    t = setTimeout(async () => {
      const count = await __getServerItemCount();
      if (count == null) return;
      document.dispatchEvent(
        new CustomEvent('cart:count-changed', { detail: { count } })
      );
    }, 30);
  };

  // Patch fetch
  const nativeFetch = window.fetch;
  window.fetch = async function (input, init) {
    const res = await nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : input.url;
      const method = ((init && init.method) || (typeof input !== 'string' && input.method) || 'GET').toUpperCase();
      if (res.ok && url && url.includes('/cart') && method !== 'GET') notify();
    } catch {}
    return res;
  };

  // Patch XHR
  const XHR = window.XMLHttpRequest;
  const open = XHR.prototype.open;
  const send = XHR.prototype.send;
  XHR.prototype.open = function (method, url) {
    this.__cartWatch = { method: (method || 'GET').toUpperCase(), url: String(url || '') };
    return open.apply(this, arguments);
  };
  XHR.prototype.send = function () {
    this.addEventListener('load', () => {
      const w = this.__cartWatch || {};
      if (this.status >= 200 && this.status < 300 && w.url && w.url.includes('/cart') && w.method !== 'GET') notify();
    });
    return send.apply(this, arguments);
  };
}

/* ------------------------------------------------------------------
   CART ICON COMPONENT
   ------------------------------------------------------------------ */

class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    __installCartNetworkWatchers();

    // Listen to both events:
    document.addEventListener(ThemeEvents.cartUpdate, this.onThemeCartUpdate);
    document.addEventListener('cart:count-changed', this.onSilentCartUpdate);

    this.ensureCartBubbleIsCorrect(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onThemeCartUpdate);
    document.removeEventListener('cart:count-changed', this.onSilentCartUpdate);
  }

  // From theme → may open drawer
  onThemeCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  // From our watcher → silent, absolute count
  onSilentCartUpdate = (event) => {
    const itemCount = event.detail.count ?? 0;
    this.renderCartBubble(itemCount, false);
  };

  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', this.currentCartCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = async (mayFetchServer = false) => {
    const ss = sessionStorage.getItem('cart-count');
    const visible = this.refs.cartBubbleCount.textContent;

    if (ss === visible || ss === null) {
      if (mayFetchServer) {
        const serverCount = await __getServerItemCount();
        if (serverCount != null) this.renderCartBubble(serverCount, false, false);
      }
      return;
    }

    try {
      const { value, timestamp } = JSON.parse(ss);
      const fresh = typeof timestamp === 'number' && (Date.now() - timestamp) < 10000;
      const count = parseInt(value, 10);
      if (fresh && count >= 0) {
        this.renderCartBubble(count, false, false);
      } else if (mayFetchServer) {
        const serverCount = await __getServerItemCount();
        if (serverCount != null) this.renderCartBubble(serverCount, false, false);
      }
    } catch {
      if (mayFetchServer) {
        const serverCount = await __getServerItemCount();
        if (serverCount != null) this.renderCartBubble(serverCount, false, false);
      }
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
